import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import { verifyHypPayment } from '../features/orders/orderServices';
import Loader from '../components/Loader';
import './OrderSuccess.css';

/**
 * Hyp redirects here after payment.
 * If loaded inside the checkout iframe, break out to the top window
 * so checkout continues on the full site.
 *
 * Portal success URL (production): https://www.irony-x.com/payment/success
 */
const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If Hyp redirected inside the iframe, continue checkout in the top window
    if (window.self !== window.top) {
      try {
        window.top!.location.href = window.location.href;
      } catch {
        window.location.href = window.location.href;
      }
      return;
    }

    let cancelled = false;

    const run = async () => {
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });

      if (!params.Order && !params.Id) {
        setError(t('paymentSuccess.missingParams'));
        return;
      }

      try {
        const result = await verifyHypPayment(params);
        if (cancelled) return;

        if (result.paid && result.order) {
          clearCart();
          sessionStorage.removeItem('hypPendingOrder');
          const orderNumber =
            result.order.orderNumber?.toString() ||
            params.Order ||
            result.order._id;
          navigate(`/order-success/${orderNumber}`, { replace: true });
          return;
        }

        navigate('/payment-failed', { replace: true });
      } catch (err: any) {
        if (cancelled) return;
        console.error('Hyp verify failed:', err);
        setError(
          err?.response?.data?.error || t('paymentSuccess.verifyFailed')
        );
        setTimeout(() => {
          navigate('/payment-failed', { replace: true });
        }, 2500);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, clearCart, t]);

  return (
    <div className="order-success-page">
      <div className="container">
        <div className="success-content">
          {error ? (
            <>
              <h1>{t('paymentSuccess.errorTitle')}</h1>
              <p className="confirmation-text">{error}</p>
            </>
          ) : (
            <>
              <Loader size="large" text={t('paymentSuccess.verifying')} />
              <p className="confirmation-text" style={{ marginTop: 16 }}>
                {t('paymentSuccess.pleaseWait')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
